<template>
    <div class="PostImagesUpload">
        <el-button
                type="primary"
                size="mini"
                class="mb-4"
                @click.prevent.native="handleAddClick"
                :disabled="desactiverChamp"
        >{{ $t("posts.components.picturesButton") }}
        </el-button>

        <el-dialog class="PostImagesUpload-dialog" :visible="dialogVisible" @close="closeDialog()">
            <h3 slot="title">{{ $t("posts.components.picturesDialogTitle") }}</h3>
            <el-row
                    :gutter="44"
                    class="PostImagesUpload-container"
                    :type="browserContext.up('md') ? 'flex' : null"
            >
                <el-col :lg="24" class="pb-3">
                    <p>{{ $t("posts.components.picturesDialogTextTop") }} :</p>
                    <el-upload
                        accept="image/jpeg, image/jpg, image/png, image/webp, image/gif, image/heic, image/avif"
                        action=""
                            class="upload-demo"
                            drag
                            :on-change="handleFileSelect"
                            :auto-upload="false"
                            multiple
                            :file-list="fileList"
                            :limit="maximumPhotosAuthorizedToUpload"
                    >
                        <em class="el-icon-upload"></em>
                        <div class="el-upload__text" v-html="$t('posts.components.picturesDialogTextMiddle')"/>
                        <div class="el-upload__tip" slot="tip" v-html="uploadTipText"/>
                    </el-upload>
                </el-col>
            </el-row>

            <div class="PostImagesUpload-actions">
                <el-button @click.prevent.native="dialogVisible = false" :disabled="convertingHeic">
                    {{ $t("posts.components.picturesButtonCancel") }}
                </el-button>
                <el-button @click.prevent.native="addImages" type="primary" :loading="convertingHeic">
                    {{ convertingHeic ? $t("posts.components.picturesButtonConverting") : $t("posts.components.picturesButtonAdd") }}
                </el-button>
            </div>
        </el-dialog>

<!--        TODO: check https://sortablejs.github.io/Vue.Draggable/#/transition-example-2 to improve design-->
        <el-row :gutter="50">
            <draggable :list="post.photos" group="people" @start="drag=true" @end="drag=false" v-bind="dragOptions" @change="onChange">
                <el-col :span="8" v-for="photo in post.photos" :key="photo.url" style="padding-bottom: 20px"  v-if="photo != undefined">
                    <ubi-card :published="true" full-width>
                        <img :key="photo.url" :src="photo.url" height="215" alt="" />
                        <template slot="footer">
                            <el-button type="danger" size="mini" @click.prevent.native="handleRemove(photo)" :disabled="desactiverChamp">
                                {{ $t("posts.components.picturesButtonDelete") }}
                            </el-button>
                        </template>
                    </ubi-card>
                </el-col>
            </draggable>
        </el-row>
    </div>
</template>

<style lang="scss">
    @import "@/theme/_bootstrap.scss";

    .PostImagesUpload {
        &-dialog .el-dialog {
            width: 670px;
            max-width: 90%;
        }

        &-container {
            > *:not(:first-child) {
                border-left: 1px solid $gray-200;
            }
        }

        &-actions {
            text-align: right;
        }
    }

    .button {
        margin-top: 35px;
    }
    .flip-list-move {
        transition: transform 0.5s;
    }
    .no-move {
        transition: transform 0s;
    }
    .ghost {
        opacity: 0.5;
        background: #c8ebfb;
    }
    .list-group {
        min-height: 20px;
    }
    .list-group-item {
        cursor: move;
    }
    .list-group-item i {
        cursor: pointer;
    }
</style>

<script>
import { Dialog, Upload } from 'element-ui'
import draggable from 'vuedraggable'
import { isHeicFile, convertHeicToJpeg } from '@/utils/heicConverter'
import { mapGetters } from 'vuex';

export default {
  name: 'PostImagesUpload',
  inject: ['browserContext'],
  components: { ElDialog: Dialog, ElUpload: Upload, draggable },
  props: ['desactiverChamp', 'post', 'value', 'selected'],
  data () {
    return {
      file: '',
      fileList: [],
      dialogVisible: false,
      convertingHeic: false,
      maxPhotos: 20 // Valeur d'initialisation
    }
  },
  methods: {
    handleAddClick (event) {
      this.dialogVisible = true
    },
    handleFileSelect (file, fileList) {
      // Vérifier la taille (10 MB max)
      if (file.size > 10000000) {
        this.closeDialog()
        this.$message({
          message: this.$t('error.fileTooLarge', { maxSize: '10 MB' }),
          type: 'error'
        })
        return
      }

      // Types acceptés : PNG, JPEG, WebP, AVIF, HEIC
      const acceptedTypes = ['image/png', 'image/jpeg', 'image/webp', 'image/avif', 'image/gif', 'image/heic', 'image/heif']
      const fileIsHeic = isHeicFile(file)

      // Accepter aussi les HEIC même si le type MIME est vide (fallback sur extension)
      const isAcceptedType = acceptedTypes.includes(file.raw.type) || fileIsHeic

      if (isAcceptedType) {
        this.fileList.push(file)

        // Informer l'utilisateur pour les fichiers HEIC
        if (fileIsHeic) {
          this.$message({
            message: this.$t('success.heicWillBeConverted', { filename: file.name }),
            type: 'info',
            duration: 3000
          })
        }
      } else {
        this.closeDialog()
        this.$message({
          message: this.$t('error.formatImageNotAllowed') || 'Format d\'image non autorisé. Formats acceptés: JPG, PNG, WebP, HEIC',
          type: 'error'
        })
      }
    },
    closeDialog () {
      this.dialogVisible = false
    },
    async addImages () {
      const self = this
      const pointeur = self.post.photos.length

      // Vérifier si on a des fichiers HEIC à convertir
      const hasHeicFiles = this.fileList.some(file => isHeicFile(file))

      if (hasHeicFiles) {
        self.convertingHeic = true
      }

      try {
        // Convertir tous les fichiers en parallèle
        const promises = this.fileList.map(async (file, index) => {
          const fileIsHeic = isHeicFile(file)

          if (fileIsHeic) {
            try {
              // Convertir le HEIC en JPEG côté client
              const jpegDataUrl = await convertHeicToJpeg(file.raw, 0.9)

              const newPhoto = {
                type: 'base64',
                url: jpegDataUrl
              }

              if (!self.post.photos) {
                self.post.photos = []
              }
              self.post.photos[pointeur + index] = newPhoto
              self.$forceUpdate()
            } catch (error) {
              console.error('Erreur conversion HEIC:', error)
              self.$message({
                message: self.$t('error.heicConversionFailed', { filename: file.name }),
                type: 'error',
                duration: 5000
              })
            }
          } else {
            // Pour les autres formats, créer une prévisualisation normale
            return new Promise((resolve) => {
              const previewReader = new FileReader()

              previewReader.onload = function (readerEvt) {
                const newPhoto = { type: 'base64', url: readerEvt.target.result }
                if (!self.post.photos) {
                  self.post.photos = []
                }
                self.post.photos[pointeur + index] = newPhoto
                self.$forceUpdate()
                resolve()
              }

              previewReader.readAsDataURL(file.raw)
            })
          }
        })

        // Attendre que toutes les conversions soient terminées
        await Promise.all(promises)
      } finally {
        self.convertingHeic = false
        this.dialogVisible = false
        this.fileList = []
      }
    },
    handleRemove (photo) {
      this.post.photos.splice(this.post.photos.findIndex(v => v.url === photo.url), 1)
      this.$forceUpdate()
    },
    onChange () {
      this.$forceUpdate()
    }
  },
  watch: {
    maxPhotosAllowed: {
      handler (newValue, oldValue) {
        if (newValue) {
          this.maxPhotos = newValue
        }
      },
      immediate: true
    }
  },
  computed: {
    ...mapGetters(['getNbPhotosMaxAnnonce']),
    dragOptions () {
      return {
        animation: 0,
        group: 'description',
        disabled: false,
        ghostClass: 'ghost'
      }
    },
    maxPhotosAllowed () {
      return this.getNbPhotosMaxAnnonce
    },
    maximumPhotosAuthorizedToUpload () {
      if (this.post.photos) {
        return this.maxPhotosAllowed - this.post.photos.length
      } else {
        return this.maxPhotosAllowed
      }
    },
    uploadTipText () {
      return this.$t('posts.components.picturesDialogTextBottom', { max: this.maxPhotos })
    }
  },
  beforeMount () {
    if (!this.post.photos) {
		   this.post.photos = []
    }
  }
}
</script>
